import React, { useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { Provider as ReduxProvider } from 'react-redux';
import { ThemeProvider } from '@actinc/dls';
import { store } from 'store';
import PrivateRoute from 'core/PrivateRoute';
import { useTranslation } from 'react-i18next';
import CleavePhone from 'cleave.js/dist/addons/cleave-phone.us'; //required for cleave phone, even is not used in this file
import { IrregularitiesContainer } from 'features/irregularities/';
import { DashboardContainer } from 'features/dashboard/';
import { TcEventDashboard, RoomsDashboard } from 'features/tc-event-dashboard/';
import { TCRenewalContainer } from 'features/tc-renewal/';
import { EventChecklistContainer } from 'features/event-checklist/';
import { EventRoomsContainer } from 'features/room-builder/';
import { ResetContainer, ChangeKnownPassword } from 'features/password-reset/';
import { TrainingContainer } from 'features/training/';
import { NotificationsContainer } from 'features/notifications/';
import { MaterialChainDashboard, EventRanges, RoomRanges } from 'features/material-chain';
import { SeatingChartContainer } from 'features/seating-chart';
import { RoomRoster } from 'features/room-roster';
import { RostersDashboard } from 'features/rosters';
import { ProfileContainer, SendEmailContainer } from 'features/profile/';
import { TcDashboard } from 'features/tc/';
import { StaffAndRoomsContainer } from 'features/staff-and-rooms/';
import { ShipmentTracking } from 'features/shipment-tracking/';
import { OnlineTestingDashboard } from 'features/online-testing';
import {
  RoomDashboard,
  ViewTasks,
  ManualTimer,
  CloseRoom,
  MarkTestComplete,
} from 'features/task-management/';
import { PageNotFound, StickyBanner, IdleWatch, NoInternetModal } from 'shared/components';
import { Navbar, Subnavbar, Footer, EventNavbar } from 'features/navigation/';
import { Register } from 'features/register/';
import Login from 'features/login/Login';
import HelpContainer from 'features/help/Help';
import * as userActions from 'core/duck';
import Auth from 'core/services/authToken';
import findIndex from 'lodash/findIndex';
import 'assets/main.scss';
import theme from './theme';

const LoginRegister = () => {
  const { t } = useTranslation();
  if (Auth.authToken) {
    globalThis.location.pathname = '/dashboard';
    return null;
  }

  setTimeout(
    () =>
      Array.from(document.querySelectorAll('[data-bs-toggle="tooltip"]')).forEach((el) =>
        bootstrap.Tooltip.getOrCreateInstance(el).enable()
      ),
    500
  );

  return (
    <div className="mr-auto ml-auto mt-5 login-container">
      <Link id="LoginLink" to="/login" href="/login" className="btn btn-primary d-block">
        {t('login.loginLinkLabel')}
      </Link>
      <Link to="/register" className="btn btn-primary d-block">
        {t('register.createAccountLinkLabel')}
      </Link>
    </div>
  );
};

const onIdle = () => {
  userActions.logoutUser()(store.dispatch);
};

// Layout component that wraps all routes
const AppLayout = () => {
  const location = useLocation();
  const currentPath = location.pathname;
  const width = window.innerWidth;
  const isMobile = width < 768;
  const allowed = [
    '/dashboard',
    '/training',
    '/profile',
    '/irregularities',
    '/help',
    '/notifications',
  ];
  const hasSubNav = currentPath !== '/' && allowed.includes(currentPath);
  const contentClass = !isMobile ? 'main-container' : '';
  const fullWidthPages = [
    '/test-center-renewal',
    '/test-center-establishment',
    'seating-chart',
    '/rosters',
    'staff',
  ];
  const isFullWidth = findIndex(fullWidthPages, (p) =>
    currentPath.indexOf(p) !== -1 ? true : false
  );
  const [isExpanded, setIsExpanded] = useState(width > 1200);

  return (
    <ThemeProvider theme={theme}>
      <ReduxProvider store={store}>
        <div>
          <Navbar />
          <StickyBanner isExpanded={isExpanded}>
            <div className="sticky-element" />
          </StickyBanner>
          {isMobile && <EventNavbar />}
          <NoInternetModal />
          <main className={isFullWidth !== -1 ? 'pb-5 container w-100' : 'pb-5 container'}>
            <div className="row">
              {currentPath.includes('/dashboard') ? null : isMobile ? (
                hasSubNav && <Subnavbar isMobile={isMobile} />
              ) : (
                <Subnavbar
                  isMobile={isMobile}
                  setIsExpanded={setIsExpanded}
                  isExpanded={isExpanded}
                />
              )}
              <div
                className={`content col ${contentClass} ${currentPath
                  .replace(/\d+/, '')
                  .replace(/\/\//, '-')
                  .replace(/\//, '')
                  .replace(/\d+/, '')
                  .replace(/\/\//, '-')
                  .replace('/', '')
                  .replace('/', '')}`}>
                <Outlet />
              </div>
            </div>
          </main>
          <Footer />
          <IdleWatch onIdle={onIdle} />
        </div>
      </ReduxProvider>
    </ThemeProvider>
  );
};

// Export routes configuration for createBrowserRouter
export const routes = [
  {
    element: <AppLayout />,
    children: [
      { path: '/', element: <LoginRegister /> },
      { path: '/login', element: <Login /> },
      { path: '/forgot-password/*', element: <ResetContainer /> },
      { path: '/register/:step?', element: <Register /> },
      {
        path: '/dashboard',
        element: <PrivateRoute component={DashboardContainer} />,
      },
      {
        path: '/change-password',
        element: <PrivateRoute component={ChangeKnownPassword} />,
      },
      {
        path: '/test-event/:eventId',
        element: <PrivateRoute component={TcEventDashboard} />,
      },
      {
        path: '/test-event/:eventId/rooms-dashboard',
        element: <PrivateRoute component={RoomsDashboard} />,
      },
      {
        path: '/test-event/:eventId/staff-and-rooms',
        element: <PrivateRoute component={StaffAndRoomsContainer} />,
      },
      {
        path: '/test-event/:eventId/room/:roomId',
        element: <PrivateRoute component={RoomDashboard} />,
      },
      {
        path: '/test-event/:eventId/room/:roomId/manual-timer/:timerIdx',
        element: <PrivateRoute component={ManualTimer} />,
      },
      {
        path: '/test-event/:eventId/room/:roomId/mark-test-complete',
        element: <PrivateRoute component={MarkTestComplete} />,
      },
      {
        path: '/test-event/:eventId/room/:roomId/online-testing',
        element: <PrivateRoute component={OnlineTestingDashboard} />,
      },
      {
        path: '/test-event/:eventId/room/:roomId/close-room',
        element: <PrivateRoute component={CloseRoom} />,
      },
      {
        path: '/test-event/:eventId/rooms/*',
        element: <PrivateRoute component={EventRoomsContainer} />,
      },
      {
        path: '/test-event/:eventId/room/:roomId/tasks',
        element: <PrivateRoute component={ViewTasks} />,
      },
      {
        path: '/test-event/:eventId/room/:roomId/seating-chart/*',
        element: <PrivateRoute component={SeatingChartContainer} />,
      },
      {
        path: '/test-event/:eventId/room/:roomId/roster/*',
        element: <PrivateRoute component={RoomRoster} />,
      },
      {
        path: '/test-event/:eventId/rosters/:locationEventId/*',
        element: <PrivateRoute component={RostersDashboard} />,
      },
      {
        path: '/test-event/:eventId/event-checklist/*',
        element: <PrivateRoute component={EventChecklistContainer} />,
      },
      {
        path: '/test-event/:eventId/test-materials',
        element: <PrivateRoute component={MaterialChainDashboard} />,
      },
      {
        path: '/test-event/:eventId/test-materials/ranges',
        element: <PrivateRoute component={EventRanges} />,
      },
      {
        path: '/test-event/:eventId/room/:roomId/ranges',
        element: <PrivateRoute component={RoomRanges} />,
      },
      {
        path: '/test-center-renewal/:step/:tcCycleId',
        element: <PrivateRoute component={TCRenewalContainer} />,
      },
      {
        path: '/test-center-establishment/:step/:tcCycleId',
        element: <PrivateRoute component={TCRenewalContainer} />,
      },
      {
        path: '/irregularities/:step/:eventId/:roomId?',
        element: <PrivateRoute component={IrregularitiesContainer} />,
      },
      {
        path: '/profile/*',
        element: <PrivateRoute component={ProfileContainer} />,
      },
      {
        path: '/help',
        element: <PrivateRoute component={HelpContainer} />,
      },
      {
        path: '/email-us',
        element: <PrivateRoute component={SendEmailContainer} />,
      },
      {
        path: '/training/*',
        element: <PrivateRoute component={TrainingContainer} />,
      },
      {
        path: '/notifications',
        element: <PrivateRoute component={NotificationsContainer} />,
      },
      {
        path: '/test-center/:tcId',
        element: <PrivateRoute component={TcDashboard} />,
      },
      {
        path: '/test-event/:eventId/shipment-tracking',
        element: <PrivateRoute component={ShipmentTracking} />,
      },
      { path: '*', element: <PageNotFound /> },
    ],
  },
];

const App = () => {
  return null; // This component is no longer used with createBrowserRouter
};

export default App;
